import { sleep } from "./util/sleep.js";
import { logError } from "./util/log-error.js";
import { DynamoDBClient, } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocument, BatchGetCommand, BatchWriteCommand, QueryCommand, PutCommand, GetCommand, UpdateCommand, ScanCommand, DeleteCommand, } from "@aws-sdk/lib-dynamodb";
const marshallOptions = {
    // Whether to automatically convert empty strings, blobs, and sets to `null`.
    convertEmptyValues: false,
    // Whether to remove undefined values while marshalling.
    removeUndefinedValues: true,
    // Whether to convert typeof object to map attribute.
    convertClassInstanceToMap: false,
};
const unmarshallOptions = {
    // Whether to return numbers as a string instead of converting them to native JavaScript numbers.
    wrapNumbers: false,
};
const translateConfig = { marshallOptions, unmarshallOptions };
async function getItem(db, params) {
    const response = await db.send(new GetCommand(params));
    return response?.Item;
}
const READ_BATCH_SIZE = 100;
async function batchGet(db, table, keys, { consistentRead = false } = {}) {
    const batchGets = [];
    for (let i = 0; i < keys.length; i += READ_BATCH_SIZE) {
        const slice = keys.slice(i, i + READ_BATCH_SIZE);
        const params = {
            RequestItems: {
                [table]: {
                    Keys: slice,
                },
            },
        };
        batchGets.push(db.send(new BatchGetCommand(params)));
    }
    const results = await Promise.all(batchGets);
    let items = results.flatMap((r) => r.Responses?.[table] ?? []);
    let unprocessedKeys = results
        .map((r) => r.UnprocessedKeys?.[table]?.Keys ?? [])
        .filter((keys) => keys.length > 0);
    const maxRetries = 5;
    let retries = 0;
    while (unprocessedKeys.length && retries < maxRetries) {
        const waitTime = 2 ** retries * 1000;
        await sleep(waitTime);
        const retryGets = [];
        for (const keys of unprocessedKeys) {
            retryGets.push(db.send(new BatchGetCommand({
                RequestItems: {
                    [table]: {
                        ConsistentRead: consistentRead,
                        Keys: keys,
                    },
                },
            })));
        }
        const retryResults = await Promise.all(retryGets);
        items = items.concat(retryResults.flatMap((r) => r.Responses?.[table] ?? []));
        unprocessedKeys = retryResults
            .map((r) => r.UnprocessedKeys?.[table]?.Keys ?? [])
            .filter((keys) => keys.length > 0);
        retries++;
    }
    return items;
}
const WRITE_BATCH_SIZE = 25;
async function batchPut(db, table, items) {
    const batchWrites = [];
    for (let i = 0; i < items.length; i += WRITE_BATCH_SIZE) {
        const slice = items.slice(i, i + WRITE_BATCH_SIZE);
        const params = {
            RequestItems: {
                [table]: slice.map((item) => {
                    return {
                        PutRequest: {
                            Item: item,
                        },
                    };
                }),
            },
        };
        batchWrites.push(db.send(new BatchWriteCommand(params)));
    }
    const results = await Promise.all(batchWrites);
    let unprocessedWrites = results.filter((r) => Object.keys(r.UnprocessedItems || {}).length > 0);
    const maxRetries = 5;
    let retries = 0;
    while (unprocessedWrites.length && retries < maxRetries) {
        const waitTime = 2 ** retries * 1000;
        await sleep(waitTime);
        const retryWrites = [];
        for (const write of unprocessedWrites) {
            retryWrites.push(db.send(new BatchWriteCommand({
                RequestItems: write.UnprocessedItems,
            })));
        }
        const retryResults = await Promise.all(retryWrites);
        unprocessedWrites = retryResults.filter((r) => Object.keys(r.UnprocessedItems || {}).length > 0);
        retries++;
    }
    if (unprocessedWrites.length) {
        console.error(unprocessedWrites);
        throw new Error("Failed to process all writes in batch");
    }
}
async function batchDelete(db, table, items) {
    const batchWrites = [];
    for (let i = 0; i < items.length; i += WRITE_BATCH_SIZE) {
        const slice = items.slice(i, i + WRITE_BATCH_SIZE);
        const params = {
            RequestItems: {
                [table]: slice.map((item) => {
                    return {
                        DeleteRequest: {
                            Key: {
                                PK: item.PK,
                                SK: item.SK,
                            },
                        },
                    };
                }),
            },
        };
        batchWrites.push(db.send(new BatchWriteCommand(params)));
    }
    const results = await Promise.all(batchWrites);
    let unprocessedWrites = results.filter((r) => Object.keys(r.UnprocessedItems || {}).length > 0);
    const maxRetries = 5;
    let retries = 0;
    while (unprocessedWrites.length && retries < maxRetries) {
        const waitTime = 2 ** retries * 1000;
        await sleep(waitTime);
        const retryWrites = [];
        for (const write of unprocessedWrites) {
            retryWrites.push(db.send(new BatchWriteCommand({
                RequestItems: write.UnprocessedItems,
            })));
        }
        const retryResults = await Promise.all(retryWrites);
        unprocessedWrites = retryResults.filter((r) => Object.keys(r.UnprocessedItems || {}).length > 0);
        retries++;
    }
    if (unprocessedWrites.length) {
        console.error(unprocessedWrites);
        throw new Error("Failed to process all writes in batch");
    }
}
async function query(db, params) {
    try {
        let items = [];
        let ExclusiveStartKey;
        do {
            const response = await db.send(new QueryCommand({
                ...params,
                ExclusiveStartKey,
            }));
            ExclusiveStartKey = response.LastEvaluatedKey;
            items = items.concat(response.Items ?? []);
        } while (ExclusiveStartKey && (!params.Limit || items.length < params.Limit));
        return items;
    }
    catch (error) {
        if (error instanceof Error) {
            const msg = `${error.message}\n${JSON.stringify(params, null, 2)}`;
            logError(msg);
        }
        throw error;
    }
}
async function scan(db, params) {
    const { Items = [], LastEvaluatedKey } = await db.send(new ScanCommand({ ...params }));
    return { Items, LastEvaluatedKey };
}
async function scanAll(db, params) {
    let items = [];
    let ExclusiveStartKey;
    do {
        const response = await db.send(new ScanCommand({
            ...params,
            ExclusiveStartKey,
        }));
        ExclusiveStartKey = response.LastEvaluatedKey;
        items = items.concat(response.Items ?? []);
    } while (ExclusiveStartKey && (!params.Limit || items.length < params.Limit));
    return items;
}
async function putItem(db, params) {
    return db.send(new PutCommand(params));
}
async function updateItem(db, params) {
    return db.send(new UpdateCommand(params));
}
async function deleteItem(db, params) {
    return db.send(new DeleteCommand(params));
}
let db;
function initDb() {
    try {
        const dbConfig = { region: "us-west-2" };
        // if (process.env.AWS_ACCESS_KEY_ID && process.env.AWS_SECRET_ACCESS_KEY) {
        //   dbConfig.credentials = {
        //     accessKeyId: process.env.AWS_ACCESS_KEY_ID,
        //     secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY,
        //   };
        // }
        const dynamoDBClient = new DynamoDBClient(dbConfig);
        db = DynamoDBDocument.from(dynamoDBClient, translateConfig);
    }
    catch (error) {
        // console.error(error);
    }
}
initDb();
export function getDb() {
    if (!db) {
        initDb();
    }
    return {
        db,
        getItem: (params) => getItem(db, params),
        batchGet: (table, keys, options = {}) => batchGet(db, table, keys, options),
        batchPut: (table, items) => batchPut(db, table, items),
        batchDelete: (table, items) => batchDelete(db, table, items),
        deleteItem: (params) => deleteItem(db, params),
        query: (params) => query(db, params),
        scan: (params) => scan(db, params),
        scanAll: (params) => scanAll(db, params),
        putItem: (params) => putItem(db, params),
        updateItem: (params) => updateItem(db, params),
    };
}
//# sourceMappingURL=db.js.map